import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Sparkles, Trash2, Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";

function Review({ record, onConfirmed }) {
  const [items, setItems] = useState(record.extracted.map((i) => ({ ...i, include: true })));
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/records/${record.id}/confirm`, { items });
      toast.success(`${data.reminders_created} reminder(s) added`);
      onConfirmed();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded-2xl bg-[#FFFBF0] border border-[#FDE68A] p-4" data-testid={`record-review-${record.id}`}>
      <p className="font-semibold flex items-center gap-1.5 mb-1"><Sparkles className="h-4 w-4 text-[#D97706]" />Please check what we found</p>
      <p className="text-xs text-slate-500 mb-3">Nothing is saved until you confirm. Fix any name or date that looks wrong.</p>
      {items.length === 0 && <p className="text-sm text-slate-600">We couldn't find vaccine or preventive due dates. You can add reminders manually.</p>}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="bg-white rounded-xl p-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-center">
            <input type="checkbox" className="h-5 w-5 accent-[#F59E0B]" checked={it.include} data-testid={`record-item-${i}-include`} onChange={(e) => upd(i, "include", e.target.checked)} />
            <input className="pl-input !min-h-[40px]" value={it.name} data-testid={`record-item-${i}-name`} onChange={(e) => upd(i, "name", e.target.value)} />
            <span className="text-xs text-slate-500">Due</span>
            <input type="date" className="pl-input !min-h-[40px]" value={it.due_date || ""} data-testid={`record-item-${i}-due`} onChange={(e) => upd(i, "due_date", e.target.value)} />
            {it.date_given && <><span /><span className="text-xs text-slate-500">Last given {it.date_given}</span></>}
          </div>
        ))}
      </div>
      <button disabled={busy} onClick={confirm} data-testid="record-confirm-btn" className="pl-btn-primary w-full mt-3">Confirm & add reminders</button>
      <p className="text-[11px] text-slate-400 mt-2">Items without a due date are skipped. Questions about vaccines? Please contact your veterinarian.</p>
    </div>
  );
}

export function VetRecords({ petId, onChange }) {
  const [records, setRecords] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/pets/${petId}/records`).then((r) => setRecords(r.data)), [petId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      setBusy("Uploading...");
      const { data } = await api.post(`/pets/${petId}/records`, fd);
      setBusy("Reading your paperwork (about 20 seconds)...");
      await api.post(`/records/${data.id}/extract`).catch((err) => toast.error(errMsg(err)));
    } catch (err) { toast.error(errMsg(err)); }
    setBusy("");
    load();
  };
  const view = async (r) => {
    const res = await api.get(`/records/${r.id}/file`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  };
  const remove = async (r) => { await api.delete(`/records/${r.id}`); load(); };

  return (
    <div data-testid="vet-records-section">
      <label className="block rounded-2xl border-2 border-dashed border-[#F59E0B] bg-[#FFFBF0] p-6 text-center cursor-pointer" data-testid="vet-record-upload-dropzone">
        <Upload className="h-7 w-7 mx-auto text-[#D97706]" />
        <p className="font-heading font-semibold mt-2">{busy || "Upload vet paperwork"}</p>
        <p className="text-xs text-slate-500">Photo or PDF. AI reads vaccine names and due dates for you to confirm.</p>
        <input type="file" accept="image/*,application/pdf" className="hidden" disabled={!!busy} data-testid="vet-record-file-input" onChange={upload} />
      </label>
      <div className="space-y-3 mt-4">
        {records.map((r) => (
          <div key={r.id} className="pl-card p-4" data-testid={`record-${r.id}`}>
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-slate-400" />
              <div className="flex-1 min-w-0"><p className="font-semibold truncate">{r.filename}</p>
                <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()} · {r.status === "confirmed" ? "Confirmed" : r.status === "review" ? "Needs your review" : "Uploaded"}</p></div>
              <button onClick={() => view(r)} data-testid={`record-view-${r.id}`} className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"><Eye className="h-4 w-4" /></button>
              <button onClick={() => remove(r)} data-testid={`record-delete-${r.id}`} className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"><Trash2 className="h-4 w-4" /></button>
            </div>
            {r.status === "review" && <Review record={r} onConfirmed={() => { load(); onChange?.(); }} />}
            {r.status === "uploaded" && <button className="text-sm font-semibold text-[#D97706] mt-2" data-testid={`record-extract-${r.id}`} onClick={async () => { setBusy("Reading..."); await api.post(`/records/${r.id}/extract`).catch((err) => toast.error(errMsg(err))); setBusy(""); load(); }}>Read with AI</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
